import { INCIDENT_DETAILS } from '@elementary/common-ui/constants/queryKeys';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createIncidentSummary,
  updateIncidentSummary,
  deleteIncidentSummary,
} from 'incidents/api';
import { IncidentSummary } from 'incidents/types';
import useEnvironments from 'shared/hooks/useEnvironments';

const USER_SUMMARY_TYPE = 'USER_SUMMARY';
const USER_SUMMARY_TYPE_API = 'user_summary';

type UseIncidentSummaryParams = {
  incidentId: string;
  summaries: IncidentSummary[];
};

const useIncidentSummary = ({
  incidentId,
  summaries,
}: UseIncidentSummaryParams) => {
  const { selectedEnvId } = useEnvironments();
  const hasPermission = useEnvPermission();
  const queryClient = useQueryClient();

  const userSummary = summaries.find(
    (s) => s.summaryType === USER_SUMMARY_TYPE,
  );

  const invalidateIncidentDetails = () => {
    queryClient.invalidateQueries([INCIDENT_DETAILS, incidentId]);
  };

  const { mutateAsync: saveSummary, isLoading: isSaving } = useMutation(
    async (content: string) => {
      if (!selectedEnvId) return;

      if (userSummary) {
        return updateIncidentSummary(
          selectedEnvId,
          incidentId,
          USER_SUMMARY_TYPE_API,
          content,
        );
      }
      return createIncidentSummary(
        selectedEnvId,
        incidentId,
        USER_SUMMARY_TYPE_API,
        content,
      );
    },
    {
      onSuccess: invalidateIncidentDetails,
    },
  );

  const { mutateAsync: removeSummary, isLoading: isDeleting } = useMutation(
    async () => {
      if (!selectedEnvId || !userSummary) return;

      return deleteIncidentSummary(
        selectedEnvId,
        incidentId,
        USER_SUMMARY_TYPE_API,
      );
    },
    {
      onSuccess: invalidateIncidentDetails,
    },
  );

  const canEdit = hasPermission('EDIT_INCIDENTS');

  return {
    userSummary,
    saveSummary,
    removeSummary,
    isSaving,
    isDeleting,
    canEdit,
  };
};

export default useIncidentSummary;
